import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// APOE ε4/ε4 + FADS1 + PEMT — the dietary fat and nutrient absorption story that
// standard genetic counselling almost never covers. The "hidden layer" reveal.

export const apoeDavid: Scenario = {
  slug: 'apoe-david',
  series: 'APOE & Brain Health',
  title: "He was told his risk. Nobody checked the form.",
  socialHook:
    "David, 58, was taking omega-3 and choline every day after learning he was APOE ε4/ε4. His labs stayed low. The mystery was not effort — it was the form his body could use.",

  meta: {
    description:
      "A fictional case study: David carries two copies of APOE ε4 and was already taking omega-3 and choline. FADS1 and PEMT explain why the standard supplement forms were not landing and what to discuss instead.",
  },

  persona: {
    name: 'David',
    age: 58,
    sex: 'Male',
    ancestry: 'Caribbean-British',
    occupation: 'Retired headteacher',
    chiefConcern:
      "David learned 18 months ago that he carries two copies of APOE ε4. Since then he has been anxious, taking fish oil and choline daily, eating 'clean', and doing what his family doctor suggested. His latest cholesterol is still elevated and his omega-3 and choline levels are low. He wants to know whether he has been solving the wrong problem.",
    familyHistory:
      "Father: vascular dementia, died aged 71. Paternal uncle: Alzheimer's disease, diagnosed aged 67. Mother: hypertension, no cognitive decline.",
  },

  clinicalPicture: {
    symptoms: [
      'Persistent fatigue — low energy despite 7–8 hours of sleep',
      'Mild word-finding difficulty (noticed over the past year — may be anxiety)',
      'Elevated blood pressure managed with medication',
      'Low mood and health anxiety since receiving his APOE result',
    ],
    labs: [
      { name: 'Omega-3 Index', value: '4.1%', refRange: '>8% (optimal)', flag: 'low' },
      { name: 'Plasma Choline', value: '6.2 µmol/L', refRange: '7–20 µmol/L', flag: 'low' },
    ],
    medications: [
      'Amlodipine 5mg (for blood pressure)',
      'Atorvastatin 20mg (recently started)',
    ],
    supplements: [
      'Fish oil 1,000mg daily',
      'Choline bitartrate 500mg daily',
      'Multivitamin',
    ],
    lifestyle: [
      "Diet: 'clean' — reduced processed food, more vegetables, less red meat",
      'Exercise: 30-min walk most days',
      'Non-smoker, occasional alcohol',
    ],
  },

  genetics: [
    {
      gene: 'APOE',
      variant: 'ε4/ε4',
      rsid: 'rs429358 / rs7412',
      genotype: 'Two copies of ε4',
      plainMeaning:
        "David's brain may need more help getting and holding on to healthy fats. APOE ε4/ε4 is the highest-risk APOE pattern for Alzheimer's, and it makes nutrient delivery more important.",
      technicalDetail:
        "APOE ε4/ε4 is the highest-risk APOE genotype — carried by roughly 2% of the population. It raises lifetime risk of late-onset Alzheimer's 8–12× compared to the most common ε3/ε3 type. APOE also governs how the brain handles fat and cholesterol: ε4 is less efficient at shuttling fat-soluble nutrients — including omega-3s and choline — into neurons.",
    },
    {
      gene: 'FADS1',
      variant: 'rs174537 (reduced function)',
      rsid: 'rs174537',
      genotype: 'Reduced delta-5-desaturase activity',
      plainMeaning:
        "His body struggles to turn standard omega-3 into the form the brain uses. FADS1 explains why taking fish oil did not necessarily raise the level that matters.",
      technicalDetail:
        "FADS1 encodes the enzyme that converts short-chain omega-3 (the kind in most fish oil supplements) into long-chain EPA and DHA — the forms the brain actually uses. A reduced-function variant severely impairs that conversion, which his low omega-3 index confirms despite 18 months of supplementing. For APOE ε4 carriers, adequate DHA supply matters more than average because the ε4 brain is also less efficient at recycling it.",
    },
    {
      gene: 'PEMT',
      variant: 'rs7946 (reduced function)',
      rsid: 'rs7946',
      genotype: 'Reduced phosphatidylethanolamine N-methyltransferase activity',
      plainMeaning:
        "The choline form he takes needs a conversion step his body may not do well. PEMT helps explain why daily supplementing still left his level low.",
      technicalDetail:
        "PEMT is the enzyme that synthesizes phosphatidylcholine — the usable form of choline. A reduced-function variant means David produces less of it internally, making the supplement form matter even more. Choline bitartrate requires liver conversion before it can reach the brain; CDP-choline (citicoline) and alpha-GPC bypass that step entirely, which is why his plasma choline stays low despite supplementing.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "He got the risk label, then a blank page",
      body: "Eighteen months ago, David saw three words from a commercial DNA test: APOE ε4/ε4. He read that it was the highest-risk APOE pattern for Alzheimer's and barely slept that night. His family doctor was sympathetic but had little to offer beyond keeping cardiovascular risk down. David did what he could: cleaned up his diet, walked most days, added fish oil and choline. His cholesterol is still elevated, and he feels more anxious than before.",
    },
    {
      type: 'narrative',
      heading: "The clue was in the supplements that did not show up",
      body: "David is doing the things he was told might help: omega-3 fish oil, choline, clean eating. But two blood markers give the plot away. After 18 months of fish oil, his omega-3 index is still low. His choline is low too, even with daily pills. The problem is not effort. The forms he chose do not match the way his body processes them.",
    },
    {
      type: 'reveal',
      heading: "The fish oil was the wrong route",
      body: "Most standard omega-3 advice assumes the body can convert what it takes in into the long-chain forms the brain uses. David's FADS1 variant makes that conversion inefficient, which helps explain the low omega-3 index after 18 months of supplementing. For someone with APOE ε4/ε4, DHA supply matters even more because the brain is less efficient at handling these fats. The fix is not simply more capsules. It is a more usable form.",
    },
    {
      type: 'reveal',
      heading: "The choline had the same problem",
      body: "The choline supplement David takes also depends on conversion before the brain can use it. His PEMT variant makes that step less reliable, so a daily habit can still leave the useful form low. CDP-choline and alpha-GPC take a more direct route. Same category, different form, different expected result.",
    },
    {
      type: 'action',
      heading: "Four changes to discuss with his clinician",
      items: [
        "Switch to pre-formed DHA, such as algae-derived DHA oil or triglyceride-form fish oil. These arrive in a more usable form and bypass the FADS1 conversion bottleneck.",
        "Switch choline to CDP-choline or alpha-GPC. Either form avoids the PEMT-dependent step that may be limiting him.",
        "Ask about an omega-3 index recheck in 3 months to confirm the form switch is working.",
        "Add oily fish such as sardines, mackerel, or salmon 2–3 times a week so food-based EPA/DHA supports the supplement change.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "Which form changes the picture",
      caption:
        "David was already following the generic advice. The hidden issue was form: omega-3 and choline land differently depending on the route his body can use.",
      baselineLabel: "APOE ε4/ε4 + FADS1 + PEMT baseline",
      baseline: 85,
      levers: [
        {
          label: "Switch to pre-formed DHA (algae/TG-form)",
          delta: -16,
          description: "Bypasses the FADS1 conversion bottleneck so DHA arrives in a usable form. The omega-3 index can be rechecked in 3 months.",
        },
        {
          label: "Switch choline to CDP-choline / alpha-GPC",
          delta: -10,
          description: "Bypasses PEMT and delivers a more usable choline form without relying on the sluggish conversion step.",
        },
        {
          label: "LDL to target (APOE ε4 threshold)",
          delta: -18,
          description: "Strongest modifiable lever for APOE ε4/ε4. Current statin dose is likely insufficient — worth discussing dose or addition of ezetimibe with a family doctor.",
        },
        {
          label: "Oily fish 3x/week",
          delta: -9,
          description: "Whole-food EPA/DHA compounds the supplement switch and adds anti-inflammatory benefit the capsule alone doesn't.",
        },
      ],
    },
    {
      type: 'checkin' as const,
      heading: "David's 14-day precision protocol check-in",
      goalLabel: "Omega-3 switch · CDP-choline · daily oily fish",
      caption:
        "David switched his supplements in week 1 and added sardines three times a week. Subjective changes come before labs do — energy, mood, and mental sharpness are the early signals.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Mental sharpness', value: 4 },
            { label: 'Mood / anxiety', value: 3 },
          ],
          note: "First day on algae DHA and CDP-choline. No immediate change — expected.",
        },
        {
          day: 2,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Mental sharpness', value: 4 },
            { label: 'Mood / anxiety', value: 3 },
          ],
          note: "Same. Keeping at it.",
        },
        {
          day: 3,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Mental sharpness', value: 5 },
            { label: 'Mood / anxiety', value: 4 },
          ],
          note: "Slightly clearer head this morning. Probably coincidence but noting it.",
        },
        {
          day: 4,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Mental sharpness', value: 5 },
            { label: 'Mood / anxiety', value: 4 },
          ],
          note: "Had sardines for lunch. Energy better today.",
        },
        {
          day: 5,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Mental sharpness', value: 6 },
            { label: 'Mood / anxiety', value: 5 },
          ],
          note: "Less word-finding hesitation in a conversation today. Felt notable.",
          milestone: "First noticeable shift",
        },
        {
          day: 6,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Mental sharpness', value: 5 },
            { label: 'Mood / anxiety', value: 5 },
          ],
          note: "Weekend. Relaxed. Consistent.",
        },
        {
          day: 7,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Mental sharpness', value: 6 },
            { label: 'Mood / anxiety', value: 5 },
          ],
          note: "One week in. No afternoon slump today.",
        },
        {
          day: 8,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Mental sharpness', value: 6 },
            { label: 'Mood / anxiety', value: 6 },
          ],
          note: "Mood lighter. Less health anxiety — possibly because I feel like I'm finally doing the right things.",
        },
        {
          day: 9,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Mental sharpness', value: 7 },
            { label: 'Mood / anxiety', value: 6 },
          ],
          note: "Read for 90 minutes without losing focus. That hasn't happened in months.",
          milestone: "Focus returning",
        },
        {
          day: 10,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Mental sharpness', value: 7 },
            { label: 'Mood / anxiety', value: 6 },
          ],
          note: "Mackerel for dinner. Starting to enjoy the food changes.",
        },
        {
          day: 11,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Mental sharpness', value: 8 },
            { label: 'Mood / anxiety', value: 7 },
          ],
          note: "Best day so far.",
        },
        {
          day: 12,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Mental sharpness', value: 7 },
            { label: 'Mood / anxiety', value: 7 },
          ],
          note: "Consistent. Starting to feel like a baseline shift, not just a good day.",
        },
        {
          day: 13,
          scores: [
            { label: 'Energy', value: 8 },
            { label: 'Mental sharpness', value: 8 },
            { label: 'Mood / anxiety', value: 7 },
          ],
          note: "Slept well. Woke up rested.",
        },
        {
          day: 14,
          scores: [
            { label: 'Energy', value: 8 },
            { label: 'Mental sharpness', value: 8 },
            { label: 'Mood / anxiety', value: 8 },
          ],
          note: "Two weeks in. I finally feel like I understand what I'm doing and why.",
          milestone: "Lab recheck booked",
        },
      ],
    },
    {
      type: 'streak' as const,
      heading: "David's 8-week precision protocol goals",
      goalLabel: "Daily: algae DHA + CDP-choline · Oily fish 3x/week",
      caption:
        "The supplements were easy to switch. The food habit took until week 4 to stick.",
      weeks: [
        {
          weekLabel: 'Week 1',
          days: [true, true, false, true, true, false, true],
          note: "Forgot Wednesday — old fish oil habit is persistent. Fish 2x this week.",
        },
        {
          weekLabel: 'Week 2',
          days: [true, true, true, false, true, true, false],
          note: "Five days. Oily fish 3x — hit the target for the first time.",
        },
        {
          weekLabel: 'Week 3',
          days: [true, true, true, true, false, true, true],
          note: "Six days. Fish 3x again.",
        },
        {
          weekLabel: 'Week 4',
          days: [true, true, true, true, true, true, false],
          note: "Six out of seven — first strong week. Algae DHA now feels automatic.",
        },
        {
          weekLabel: 'Week 5',
          days: [true, true, true, true, true, true, true],
          note: "First perfect week. Supplements are now part of the morning routine.",
        },
        {
          weekLabel: 'Week 6',
          days: [true, true, true, true, true, false, true],
          note: "Six days. Away from home Saturday — forgot the CDP-choline.",
        },
        {
          weekLabel: 'Week 7',
          days: [true, true, true, true, true, true, true],
          note: "Perfect week. Sleep quality noticeably better. Energy consistent.",
        },
        {
          weekLabel: 'Week 8',
          days: [true, true, true, true, true, true, false],
          note: "Six days. Three-month blood test scheduled. Feeling like a different person.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'apoe4',
    label: 'Explore your APOE genetics in Coach',
  },
}
